import {
  PLAYER_DISPLAY_NAME_MAX,
  PLAYER_DISPLAY_NAME_MIN,
  PLAYER_SEARCH_KEYWORD_MAX,
  PLAYER_SEARCH_KEYWORDS_MAX,
} from "./playerSchema";

/**
 * @param {string} displayName
 * @returns {string}
 */
export function normalizeDisplayName(displayName) {
  return (displayName || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/**
 * @param {string} displayName
 * @param {{ club?: string | null, country?: string | null, state?: string | null, city?: string | null }} [facets]
 * @returns {string[]}
 */
export function buildSearchKeywords(displayName, facets = {}) {
  const normalized = normalizeDisplayName(displayName);
  const tokens = new Set();
  const push = (t) => {
    const s = (t || "").slice(0, PLAYER_SEARCH_KEYWORD_MAX);
    if (s.length >= 2) tokens.add(s);
  };

  if (normalized) {
    push(normalized);
    normalized.split(/[\s._-]+/).forEach(push);
    const compact = normalized.replace(/[\s._-]+/g, "");
    if (compact.length >= 2) push(compact);
  }

  [facets.club, facets.country, facets.state, facets.city]
    .map((v) => normalizeDisplayName(v || ""))
    .filter(Boolean)
    .forEach((n) => {
      push(n);
      n.split(/[\s._-]+/).forEach(push);
    });

  return [...tokens].slice(0, PLAYER_SEARCH_KEYWORDS_MAX);
}

/**
 * @param {string} displayName
 * @returns {{ ok: true, displayName: string, normalizedName: string, searchKeywords: string[] } | { ok: false, error: string }}
 */
export function validateDisplayName(displayName) {
  const trimmed = (displayName || "").trim().replace(/\s+/g, " ");
  if (trimmed.length < PLAYER_DISPLAY_NAME_MIN) {
    return { ok: false, error: "Display name is required" };
  }
  if (trimmed.length > PLAYER_DISPLAY_NAME_MAX) {
    return { ok: false, error: `Display name must be at most ${PLAYER_DISPLAY_NAME_MAX} characters` };
  }
  const normalizedName = normalizeDisplayName(trimmed);
  return {
    ok: true,
    displayName: trimmed,
    normalizedName,
    searchKeywords: buildSearchKeywords(trimmed),
  };
}

/**
 * Client-side filter when Firestore returns a candidate set.
 * @param {import('./playerTypes').PlayerRecord} player
 * @param {string} query
 */
export function playerMatchesQuery(player, query) {
  const q = normalizeDisplayName(query);
  if (!q) return true;
  if (player.normalizedName.includes(q)) return true;
  return (player.searchKeywords || []).some((kw) => kw.includes(q) || q.includes(kw));
}

/**
 * One row per signed-in account. Extra player documents for the same userId
 * (a create race) stay in the database but must not appear as copies in search.
 * Unclaimed players keep their own row.
 * @param {Array<{ playerId?: string, userId?: string | null }>} players
 */
export function collapsePlayersByAccount(players) {
  const seen = new Set();
  const out = [];
  for (const player of players || []) {
    if (!player) continue;
    const key = player.userId ? `user:${player.userId}` : `player:${player.playerId || ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(player);
  }
  return out;
}
